import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import HttpStatus from 'http-status';
import { USER_SERVICE_URLS } from '~/Constants/Urls.constants';
import { AuthModel } from '~/Models/DTO/Auth.model';
import {
  getLocalStorageAccessToken,
  getLocalStorageProviderUUId,
  getLocalStorageRefreshToken,
  logout,
  setLocalStorageAccessToken,
} from '../Hooks/useLocalStorage/useLocalStorage';
import { objectToFormData } from './Url.helpers';
import { ToastVariant } from '~/Hooks';

interface IRequestOptions {
  url: string;
  params?: Record<string, boolean | string | number | string[] | undefined>;
  config?: AxiosRequestConfig;
  apiVersion?: string;
  data?: any;
  customToken?: string;
  secondTry?: boolean;
  retrieveResponseHeader?: (headers: any) => void;
}

const getHeaders = ({
  providerId,
  userToken,
  apiVersion,
  customHeaders,
  configHeaders,
}: {
  providerId?: string;
  userToken?: string;
  apiVersion?: string;
  customHeaders?: any;
  configHeaders?: any;
}) => ({
  'Content-Type': 'application/json',
  Accept: 'application/json',
  'app-version': '1.0.0',
  'app-id': 'NeoPro',
  ...(providerId ? { ProviderId: providerId } : {}),
  ...(userToken ? { Authorization: `Bearer ${userToken}` } : {}),
  ...(apiVersion ? { 'api-version': apiVersion } : {}),
  ...(customHeaders || {}),
  ...(configHeaders || {}),
});

export const requestReactQuery = async <T>(requestOptions: IRequestOptions): Promise<T> => {
  const {
    url = '',
    config = {},
    params,
    apiVersion,
    data,
    customToken,
    secondTry = false,
    retrieveResponseHeader = () => {},
  } = requestOptions;

  const userToken = getLocalStorageAccessToken();
  const providerId = getLocalStorageProviderUUId();

  const { method = 'GET', headers: customHeaders } = config;
  const headers = getHeaders({
    providerId: providerId || '',
    userToken: customToken || userToken || '',
    apiVersion,
    customHeaders,
    configHeaders: config.headers,
  });

  const shouldAddQuery = params && !url?.includes('?');
  const queryString = shouldAddQuery ? new URLSearchParams(params as Record<string, string>) : '';

  const sanitizedData = data || config.data;

  const requestParams: AxiosRequestConfig = {
    method,
    data: sanitizedData,
    url: `${url}${shouldAddQuery ? '?' : ''}${queryString}`,
    headers,
    validateStatus: (status: number) => status >= 200 && status < 500,
    cancelToken: config?.cancelToken || undefined,
  };

  try {
    const response = await axios.request<T>(requestParams);
    retrieveResponseHeader(response.headers);
    if (response.status >= HttpStatus.OK && response.status < HttpStatus.MULTIPLE_CHOICES) {
      return response.data;
    }
    return handleError<T>({ error: response, secondTry, requestOptions });
  } catch (er) {
    return handleError<T>({ error: er, secondTry, requestOptions });
  }
};

export const requestReactQueryRaw = (requestOptions: IRequestOptions) => {
  const { url = '', config = {}, params, apiVersion, data, customToken } = requestOptions;

  const userToken = getLocalStorageAccessToken();
  const providerId = getLocalStorageProviderUUId();

  const { method = 'GET', headers: customHeaders } = config;
  const headers = getHeaders({
    providerId: providerId || '',
    userToken: customToken || userToken || '',
    apiVersion,
    customHeaders,
    configHeaders: config.headers,
  });

  const shouldAddQuery = params && !url?.includes('?');
  const queryString = shouldAddQuery ? new URLSearchParams(params as Record<string, string>) : '';

  const sanitizedData = data || config.data;

  const requestParams: AxiosRequestConfig = {
    method,
    data: sanitizedData,
    url: `${url}${shouldAddQuery ? '?' : ''}${queryString}`,
    headers,
    validateStatus: (status: number) => status >= 200 && status < 500,
    signal: config?.signal,
  };
  return axios.request(requestParams);
};

const handleError = async <T>({
  error,
  secondTry,
  requestOptions,
}: {
  error?: any;
  secondTry?: boolean;
  requestOptions: IRequestOptions;
}) => {
  if (error?.status === HttpStatus.UNAUTHORIZED && !secondTry) {
    // check if token has expired or we got a correct unauthorized
    return CheckIfTokenExpired<T>(requestOptions);
  } else {
    if (error?.status === HttpStatus.NOT_FOUND) {
      throw { error: 'resource not found' };
    }
    throw error?.response?.data || error?.data || error;
  }
};

const CheckIfTokenExpired = async <T>(requestOptions: IRequestOptions) => {
  try {
    await axios.request({
      method: 'GET',
      url: USER_SERVICE_URLS.MY_ACCOUNT,
    });
  } catch (err) {
    if ((err as any).toString().includes('401')) {
      const newToken = await getRefreshedToken();
      if (newToken) {
        return requestReactQuery<T>({
          ...requestOptions,
          customToken: newToken,
          secondTry: true,
        });
      }
      logout();
      throw new Error('cannot get new token');
    } else {
      logout();
      throw new Error('unauthorized');
    }
  }
  logout();
  throw new Error('unauthorized');
};

const getRefreshedToken = async () => {
  const error = new Error('no access token was returned while attempting to refresh the token.');
  try {
    const refreshToken = getLocalStorageRefreshToken();

    if (refreshToken) {
      const requestParams: AxiosRequestConfig = {
        method: 'POST',
        data: objectToFormData({
          grant_type: 'refresh_token',
          client_id: 'pro',
          refresh_token: refreshToken,
        }),
        url: USER_SERVICE_URLS.ACCESS_TOKEN,
      };

      try {
        const newToken = await axios.request<AuthModel>(requestParams);
        if (!newToken.data.access_token) {
          logout();
          throw error;
        } else {
          setLocalStorageAccessToken(newToken.data.access_token);
          return newToken.data.access_token;
        }
      } catch (err) {
        logout();
        throw error;
      }
    }
  } catch (err) {
    logout();
    throw error;
  }
};

type ExtendedAxiosResponse = AxiosResponse & { isAborted: boolean };
export const getStatusMessage = (response: ExtendedAxiosResponse) => {
  let toastVariant, message;
  message = response?.data?.message;
  if (!message && !!response?.data?.error) {
    message =
      typeof response?.data?.error === 'string'
        ? response?.data?.error
        : response?.data?.error?.message;
  }

  if (response?.status >= 400 && response?.status <= 500) {
    toastVariant = ToastVariant.ERROR;
  } else {
    switch (response?.status) {
      case HttpStatus.UNAUTHORIZED:
        toastVariant = ToastVariant.ERROR;
        break;
      case 200:
        toastVariant = ToastVariant.SUCCESS;
        break;
      case 202:
        toastVariant = ToastVariant.INFO;
        break;
      case 429:
        toastVariant = ToastVariant.WARNING;
        break;
      default:
        toastVariant = ToastVariant.DEFAULT;
        break;
    }
  }
  return { toastVariant, message, status: response?.status, isAborted: response?.isAborted };
};

export const checkIsAborted = (err: any) => axios.isCancel(err);

export const generateUUID = () => {
  const cryptoObj = window.crypto || (window as any).msCrypto; // Fallback for IE 11

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = cryptoObj.getRandomValues(new Uint8Array(1))[0] % 16;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};
